import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Clock, ShieldCheck, GraduationCap, AlarmClock, Save, Briefcase, WifiIcon } from "lucide-react";
import { BillingConciergeSection } from "@/pages/BillingConcierge";
import RemoteSessionsPanel from "@/pages/RemoteSessionsPanel";
import OfficeManagerFeed from "@/components/OfficeManagerFeed";
import BuddyInsights from "@/components/BuddyInsights";

/**
 * AI Office Manager — Admin Control Hub (unified view).
 *
 * Single tabbed interface for every AI-driven admin task:
 *   - Workflows: 4 automation toggles (Timesheet Chasing, OT Guard,
 *     Probation Onboarding, License Expiry) + live activity feed.
 *   - Billing: Agency Billing Suite (Cost-Center invoicing packets,
 *     Client Billable Rates, Client Billing Reports, Billing History).
 *
 * Both surfaces share the same Pro-tier gate at the router level so a
 * lapsed subscription hides everything at once.
 */
const WORKFLOWS = [
  {
    key: "timesheet_chasing",
    icon: Clock,
    label: "Timesheet Chasing",
    subtitle: "Runs nightly · 3 AM UTC",
    body: "Emails staff about missing clock-outs older than 18 hours; sends an 'All Hours In' final reminder 24 hours before every payday so nothing gets missed on the stub.",
  },
  {
    key: "overtime_guard",
    icon: AlarmClock,
    label: "Overtime Guard",
    subtitle: "Runs weekdays · 4 PM UTC",
    body: "Alerts you on the dashboard when any active employee crosses 35 real clocked hours in a work week. One-click 'Clock Out Early' reminder to the employee is available on each alert.",
  },
  {
    key: "probation_onboarding",
    icon: ShieldCheck,
    label: "Probation Onboarding",
    subtitle: "Runs daily · 9 AM UTC",
    body: "On day 85 of hire, cross-checks every new hire's personnel file. If I-9 or W-4 is missing, emails the employee to upload before the 90-day probation anniversary.",
  },
  {
    key: "license_expiry",
    icon: GraduationCap,
    label: "License Expiry",
    subtitle: "Runs daily · 8 AM UTC",
    body: "Scans certifications uploaded to Personnel Files. When any cert's expiry_date is within 30 days, sends a heads-up to both the admin and the employee — dedup'd, so you only get pinged once per cert.",
  },
];

const TABS = [
  { key: "workflows", label: "Workflows", icon: Zap, testid: "tab-workflows" },
  { key: "billing", label: "Client Billing", icon: Briefcase, testid: "tab-billing" },
  { key: "remote", label: "Remote Sessions", icon: WifiIcon, testid: "tab-remote" },
];

export default function WorkflowSettings() {
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      const hash = window.location.hash.replace("#", "");
      if (hash === "billing" || hash === "workflows" || hash === "remote") return hash;
    }
    return "workflows";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  return (
    <div className="p-10">
      <div className="overline mb-2 flex items-center gap-2">
        <Zap className="w-4 h-4" /> § AI OFFICE MANAGER · CONCIERGE HUB
      </div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-2" data-testid="workflow-settings-title">Office Manager</h1>
      <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
        One roof for every automated admin task — worker follow-ups on the left, client-billing packets on the right.
      </p>

      {/* Tabs */}
      <div className="flex gap-0 mb-8 border-b-2 border-ink overflow-x-auto" data-testid="workflow-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={
                "px-6 py-3 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 border-2 border-ink -mb-[2px] " +
                (active ? "bg-ink text-white" : "bg-white hover:bg-secondary")
              }
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "workflows" && <WorkflowsPanel />}
      {tab === "billing" && <BillingConciergeSection />}
      {tab === "remote" && <RemoteSessionsPanel />}
      <BuddyInsights />
    </div>
  );
}

function WorkflowsPanel() {
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/company");
      const ws = data.workflow_settings || {};
      const filled = Object.fromEntries(
        WORKFLOWS.map((w) => [w.key, ws[w.key] === undefined ? true : ws[w.key]])
      );
      setCompany({ ...data, workflow_settings: filled });
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const toggle = (key) => {
    setCompany((c) => ({
      ...c,
      workflow_settings: { ...c.workflow_settings, [key]: !c.workflow_settings[key] },
    }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/company", { workflow_settings: company.workflow_settings });
      toast.success("Workflows updated · takes effect on the next cron run");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!company) return <div className="font-mono text-xs text-muted-foreground">Loading workflows…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {WORKFLOWS.map((w) => {
          const on = !!company.workflow_settings[w.key];
          const Icon = w.icon;
          return (
            <div key={w.key} className="hard-card p-5" data-testid={`workflow-card-${w.key}`}>
              <div className="flex items-start gap-4">
                <div className={"p-3 border-2 border-ink " + (on ? "bg-ink text-white" : "bg-white text-ink")}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h3 className="font-heading font-black text-xl">{w.label}</h3>
                      <div className="font-mono text-[11px] text-muted-foreground">{w.subtitle}</div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`toggle-${w.key}`}
                        checked={on}
                        onChange={() => toggle(w.key)}
                        className="w-5 h-5 accent-ink"
                      />
                      <span className="font-mono text-[11px] uppercase tracking-widest">{on ? "On" : "Off"}</span>
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">{w.body}</p>
                </div>
              </div>
            </div>
          );
        })}

        <div className="flex justify-end pt-2">
          <button
            data-testid="save-workflows"
            onClick={save}
            disabled={!dirty || busy}
            className="hard-btn px-5 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : dirty ? "Save Changes" : "Saved"}
          </button>
        </div>
      </div>

      <div>
        <OfficeManagerFeed scope="admin" />
      </div>
    </div>
  );
}
